import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SearchCheck, SearchX, Search, ClipboardCheck, ClipboardX, Clipboard, Megaphone, MegaphoneOff } from 'lucide-react';
import { VALIDATION_STATUS } from 'utils/validationResultUtils';

export const VALIDATION_COLORS = {
    [VALIDATION_STATUS.SUCCESS]: '#009160',
    [VALIDATION_STATUS.FAILED]: '#FB2C36',
    [VALIDATION_STATUS.PARTIAL]: '#E0972A',
    [VALIDATION_STATUS.INFO]: '#0288D1',
};

export const resolveValidationColor = (color, status) => {
    if (color) return color.includes('#') ? color : `#${color}`;
    return VALIDATION_COLORS[status];
};

const SEARCH_ICONS = {
    [VALIDATION_STATUS.SUCCESS]: SearchCheck,
    [VALIDATION_STATUS.FAILED]: SearchX,
    [VALIDATION_STATUS.PARTIAL]: Search,
    [VALIDATION_STATUS.INFO]: Search,
};

const CLIPBOARD_ICONS = {
    [VALIDATION_STATUS.SUCCESS]: ClipboardCheck,
    [VALIDATION_STATUS.FAILED]: ClipboardX,
    [VALIDATION_STATUS.PARTIAL]: Clipboard,
    [VALIDATION_STATUS.INFO]: Clipboard,
};

const ANNOUNCEMENT_ICONS = {
    [VALIDATION_STATUS.SUCCESS]: Megaphone,
    [VALIDATION_STATUS.FAILED]: MegaphoneOff,
    [VALIDATION_STATUS.PARTIAL]: Megaphone,
    [VALIDATION_STATUS.INFO]: Megaphone,
};

export function resolveIconAndColor({ status, color, hasAutofill, isSheetAnnouncement }) {
    let iconSet;
    let defaultIcon;

    switch (true) {
        case isSheetAnnouncement:
            iconSet = ANNOUNCEMENT_ICONS;
            defaultIcon = Megaphone;
            break;
        case hasAutofill:
            iconSet = CLIPBOARD_ICONS;
            defaultIcon = Clipboard;
            break;
        default:
            iconSet = SEARCH_ICONS;
            defaultIcon = Search;
    }

    const Icon = iconSet[status] || defaultIcon;

    return { Icon, backgroundColor: resolveValidationColor(color, status) };
}

const VIEWPORT_PADDING = 8;
const ICON_GAP = 8;
const ARROW_EDGE_INSET = 8;

function computePosition(iconEl, tooltipEl) {
    const iconRect = iconEl.getBoundingClientRect();
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const iconCenterX = iconRect.left + iconRect.width / 2;

    let top = iconRect.top - tooltipHeight - ICON_GAP;
    let placement = 'top';
    if (top < VIEWPORT_PADDING && iconRect.bottom + tooltipHeight + ICON_GAP <= vh - VIEWPORT_PADDING) {
        top = iconRect.bottom + ICON_GAP;
        placement = 'bottom';
    } else if (top < VIEWPORT_PADDING) {
        top = VIEWPORT_PADDING;
    }

    const maxLeft = vw - tooltipWidth - VIEWPORT_PADDING;
    let left = iconCenterX - tooltipWidth / 2;
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    else if (left > maxLeft) left = maxLeft;

    const arrowLeft = Math.max(ARROW_EDGE_INSET, Math.min(iconCenterX - left, tooltipWidth - ARROW_EDGE_INSET));

    return { top, left, arrowLeft, placement };
}

function samePosition(a, b) {
    return !!a && !!b && a.top === b.top && a.left === b.left && a.arrowLeft === b.arrowLeft && a.placement === b.placement;
}

export function useTooltipPosition(isActive, contentKey) {
    const [position, setPosition] = useState(null);
    const iconRef = useRef(null);
    const tooltipRef = useRef(null);
    const rafIdRef = useRef(null);

    const updatePosition = useCallback(() => {
        const iconEl = iconRef.current;
        const tooltipEl = tooltipRef.current;
        if (!iconEl || !tooltipEl) return;
        const next = computePosition(iconEl, tooltipEl);
        setPosition((prev) => (samePosition(prev, next) ? prev : next));
    }, []);

    const scheduleUpdate = useCallback(() => {
        if (rafIdRef.current !== null) return;
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            updatePosition();
        });
    }, [updatePosition]);

    useLayoutEffect(() => {
        if (!isActive) return;
        updatePosition();
    }, [isActive, contentKey, updatePosition]);

    useEffect(() => {
        if (!isActive) return undefined;
        window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });
        window.addEventListener('resize', scheduleUpdate, { passive: true });
        return () => {
            window.removeEventListener('scroll', scheduleUpdate, { capture: true });
            window.removeEventListener('resize', scheduleUpdate);
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [isActive, scheduleUpdate]);

    const resetPosition = useCallback(() => setPosition(null), []);

    return { position, iconRef, tooltipRef, resetPosition };
}
